import React from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Avatar,
  Typography,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import StarIcon from "@mui/icons-material/Star";

export interface Ambassador {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  avatar_url: string;
  avg_overall?: number;
}

interface SelectBAProps {
  ambassadors: Ambassador[];
  selectedAmbassadors: Ambassador[];
  onAmbassadorChange: (ambassadors: Ambassador[]) => void;
  onAmbassadorClick?: (ambassador: { id: number; username: string }) => void;
  multiple?: boolean;
  label?: string;
  placeholder?: string;
}

const SelectBA: React.FC<SelectBAProps> = ({
  ambassadors,
  selectedAmbassadors,
  onAmbassadorChange,
  onAmbassadorClick,
  multiple = true,
}) => {
  const theme = useTheme();

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: Ambassador
  ) => {
    return (
      <Box
        component="li"
        {...props}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 1,
          fontSize: theme.typography.body2.fontSize,
        }}
      >
        <Avatar
          src={option.avatar_url}
          alt={`${option.first_name} ${option.last_name}`}
          sx={{
            width: 32,
            height: 32,
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
            },
          }}
          onClick={(e) => {
            if (onAmbassadorClick) {
              e.stopPropagation();
              onAmbassadorClick({
                id: option.user_id,
                username: option.username,
              });
            }
          }}
        />
        <Box sx={{ flex: 1, color: theme.palette.text.primary }}>
          {`${option.first_name} ${option.last_name}`}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <StarIcon
            sx={{
              color: theme.palette.primary.main,
              fontSize: 20,
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary }}
          >
            {option.avg_overall?.toFixed(1) || "-"}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderTags = (
    selected: Ambassador[],
    getTagProps: (params: { index: number }) => any
  ) => {
    const selectedArray = Array.isArray(selected)
      ? selected
      : [selected].filter(Boolean);

    return selectedArray.map((option, index) => (
      <Chip
        {...getTagProps({ index })}
        key={option.user_id}
        label={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: theme.typography.body2.fontSize,
              color: "white",
            }}
          >
            <Avatar
              src={option.avatar_url}
              alt={`${option.first_name} ${option.last_name}`}
              sx={{ width: 24, height: 24 }}
            />
            <span>{`${option.first_name} ${option.last_name}`}</span>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <StarIcon sx={{ color: "white", fontSize: 16 }} />
              <span>{option.avg_overall?.toFixed(1) || "-"}</span>
            </Box>
          </Box>
        }
        sx={{
          backgroundColor: theme.palette.primary.main,
          borderRadius: "16px",
          "& .MuiChip-label": {
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "white",
          },
          "& .MuiChip-deleteIcon": {
            color: "white",
            "&:hover": {
              color: "rgba(255, 255, 255, 0.7)",
            },
          },
        }}
      />
    ));
  };

  return (
    <Autocomplete<Ambassador, typeof multiple>
      multiple={multiple}
      options={ambassadors.filter(
        (ambassador) =>
          !selectedAmbassadors.some(
            (selected) => selected.user_id === ambassador.user_id
          )
      )}
      value={selectedAmbassadors}
      onChange={(_, newValue) => onAmbassadorChange(newValue as Ambassador[])}
      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
      renderOption={renderOption}
      renderTags={renderTags}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Ambassador"
          placeholder=""
          variant="outlined"
          sx={{
            "& .MuiInputBase-input": {
              fontSize: theme.typography.body2.fontSize,
            },
          }}
        />
      )}
      sx={{
        "& .MuiOutlinedInput-root": {
          fontSize: theme.typography.body2.fontSize,
        },
      }}
    />
  );
};

export default SelectBA;
